import type { ZobristHash } from '../api/types';

/**
 * The canonical node route (§6.2):
 *
 *   /repertoires/:id/nodes/:hash/:positionId?path=:lineId&ply=:n
 *
 * `path`/`ply` are OPTIONAL: a node is addressable without a path context, and
 * the query string carries the (lineId, ply) pair when the user is on a line.
 */

export interface NodeRoute {
  repertoireId: string;
  zobristHash: ZobristHash;
  positionId: string;
  path: { lineId: string; ply: number } | null;
}

const HASH_RE = /^[0-9a-f]{32}$/;
const POSITION_RE = /^[0-9]+$/;

export function buildNodeRoute(route: NodeRoute): string {
  const base = `/repertoires/${encodeURIComponent(route.repertoireId)}/nodes/${route.zobristHash}/${route.positionId}`;
  if (route.path === null) return base;
  return `${base}?path=${encodeURIComponent(route.path.lineId)}&ply=${route.path.ply}`;
}

export function parseNodeRoute(pathname: string, search: string): NodeRoute | null {
  const match = /^\/repertoires\/([^/]+)\/nodes\/([0-9a-f]+)\/([0-9]+)\/?$/.exec(pathname);
  if (match === null) return null;

  const repertoireId = decodeURIComponent(match[1]);
  const zobristHash = match[2];
  const positionId = match[3];
  if (!HASH_RE.test(zobristHash) || !POSITION_RE.test(positionId)) return null;

  const params = new URLSearchParams(search);
  const lineId = params.get('path');
  const plyRaw = params.get('ply');

  let path: NodeRoute['path'] = null;
  if (lineId !== null && lineId !== '') {
    const ply = plyRaw === null ? 0 : Number.parseInt(plyRaw, 10);
    if (Number.isInteger(ply) && ply >= 0) {
      path = { lineId, ply };
    }
  }

  return { repertoireId, zobristHash, positionId, path };
}

/** Key identifying a node's API address within a repertoire. */
export function nodeKey(zobristHash: ZobristHash, positionId: string): string {
  return `${zobristHash}/${positionId}`;
}
